import React from 'react';
import { Radio, Button, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import useTranslation from '@hooks/useTranslation';
import capitalize from 'capitalize';
import styles from './PaymentMethodCard.module.css';
import { ValidStripeAddress } from '@src/api/checkout/checkoutApi';
import { ReactComponent as VisaLogo } from '@src/assets/img/interface/visa-logo.svg';
import { ReactComponent as MastercardLogo } from '@src/assets/img/interface/mastercard-logo.svg';
import { ReactComponent as AmexLogo } from '@src/assets/img/interface/amex-logo.svg';

type PaymentMethod = {
  id: string;
  address: ValidStripeAddress;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefaultMethod?: boolean;
};

type Props = {
  method: PaymentMethod;
  isSelected: boolean;
  isLoading?: boolean;
  isDeleting?: boolean;
  onSelect: (methodId: string) => void;
  onDelete?: (methodId: string) => void;
};

/**
 * Card component for displaying a payment method with visual card design
 * @param props - Component props containing payment method data and handlers
 * @returns React.JSX.Element
 */
const PaymentMethodCard = (props: Props) => {
  const { method, isSelected, isLoading, isDeleting, onSelect, onDelete } = props;
  const t = useTranslation('Checkout.Payment');

  /**
   * Gets the card logo component based on the brand
   * @param brand - The card brand (visa, mastercard, amex, etc.)
   * @returns React.JSX.Element or null
   */
  const getCardLogo = (brand: string) => {
    const normalizedBrand = brand.toLowerCase();
    
    switch (normalizedBrand) {
      case 'visa':
        return (
          <div className={styles.CardLogo}>
            <VisaLogo className={styles.SvgLogo} />
          </div>
        );
      case 'mastercard':
        return (
          <div className={styles.CardLogo}>
            <MastercardLogo className={styles.SvgLogo} />
          </div>
        );
      case 'amex':
      case 'american_express':
        return (
          <div className={styles.CardLogo}>
            <AmexLogo className={styles.SvgLogo} />
          </div>
        );
      default:
        return (
          <div className={styles.CardLogo}>
            <div className={styles.GenericLogo}>
              <span className={styles.GenericText}>
                {brand.substring(0, 4).toUpperCase()}
              </span>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`${styles.PaymentMethodCard} ${isSelected ? styles.Selected : ''}`}>
      <Radio
        checked={isSelected}
        onChange={() => onSelect(method.id)}
        disabled={isLoading || isDeleting}
        className={styles.RadioButton}
      />
      
      <div className={styles.CardVisual}>
        {getCardLogo(method.brand)}
      </div>
      
      <div className={styles.CardDetails}>
        <div className={styles.CardNumber}>
          <strong>{capitalize(method.brand)}</strong> **** **** **** {method.last4}
        </div>
        <div className={styles.CardInfo}>
          {method.expMonth.toString().padStart(2, '0')}/{method.expYear}
          {method.isDefaultMethod && (
            <span className={styles.DefaultBadge}>
              ({t.withBaseKey('CommonWords')('Default')})
            </span>
          )}
        </div>
      </div>
      
      {onDelete && (
        <Popconfirm
          title={t('DeletePaymentMethod')}
          description={t('DeletePaymentMethodConfirm')}
          onConfirm={() => onDelete(method.id)}
          okText={t.withBaseKey('CommonWords')('Yes')}
          cancelText={t.withBaseKey('CommonWords')('No')}
          disabled={isDeleting}
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            loading={isDeleting}
            disabled={isLoading || isDeleting}
            className={styles.DeleteButton}
          />
        </Popconfirm>
      )}
    </div>
  );
};

export default PaymentMethodCard;
